import { create } from 'zustand';
import type { KioskConfig } from '@museum-kiosk/shared-types';

const PROVISIONED_KEY = '__kiosk_provisioned';
const DEVICE_TOKEN_KEY = '__kiosk_device_token';

export interface KioskStore {
  // State
  isProvisioned: boolean;
  sessionId: string | null;
  config: KioskConfig | null;
  jwt: string | null;
  jwtExpiresAt: number | null;
  isOnline: boolean;

  // Actions
  setProvisioned(): void;
  deprovision(): void;
  startSession(): string;
  endSession(): void;
  setConfig(config: KioskConfig): void;
  setAuth(jwt: string, expiresAt: number): void;
  clearAuth(): void;
  setOnline(online: boolean): void;
  reset(): void;
}

const storedToken = localStorage.getItem(DEVICE_TOKEN_KEY);

export const useKioskStore = create<KioskStore>((set) => ({
  // Initial state — isProvisioned + device JWT hydrate from localStorage
  isProvisioned: localStorage.getItem(PROVISIONED_KEY) === 'true',
  sessionId: null,
  config: null,
  jwt: storedToken,
  jwtExpiresAt: storedToken ? Date.now() + 365 * 24 * 3600 * 1000 : null,
  isOnline: navigator.onLine,

  setProvisioned: () => {
    localStorage.setItem(PROVISIONED_KEY, 'true');
    set({ isProvisioned: true });
  },

  deprovision: () => {
    localStorage.removeItem(PROVISIONED_KEY);
    localStorage.removeItem(DEVICE_TOKEN_KEY);
    set({
      isProvisioned: false,
      sessionId: null,
      config: null,
      jwt: null,
      jwtExpiresAt: null,
    });
  },

  startSession: () => {
    const sessionId = crypto.randomUUID();
    set({ sessionId });
    return sessionId;
  },

  endSession: () => {
    set({ sessionId: null });
  },

  setConfig: (config: KioskConfig) => {
    set({ config });
  },

  setAuth: (jwt: string, expiresAt: number) => {
    // Device JWT persists to localStorage — cross-site cookie blocked
    // in prod browsers, so use Bearer token auth instead.
    localStorage.setItem(DEVICE_TOKEN_KEY, jwt);
    set({ jwt, jwtExpiresAt: expiresAt });
  },

  clearAuth: () => {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
    set({ jwt: null, jwtExpiresAt: null });
  },

  setOnline: (online: boolean) => {
    set({ isOnline: online });
  },

  reset: () => {
    // Clears session state but keeps isProvisioned and isOnline
    set((state) => ({
      sessionId: null,
      config: null,
      jwt: null,
      jwtExpiresAt: null,
      isProvisioned: state.isProvisioned,
      isOnline: state.isOnline,
    }));
  },
}));
